/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Logger } from '@app/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HotelBookingCancellationsService } from '@app/hotels/hotel-booking-cancellations/hotel-booking-cancellations.service';
import { ApiQuery } from '@app/core/http/api-query';
import { HotelBookingCancellation } from '@app/hotels/hotel-booking-cancellations/hotel-booking-cancellation.model';
import { Night } from '@app/hotels/rooms/night.model';
import { Room } from '@app/hotels/rooms/room.model';
import { HotelBookingsService } from '@app/hotels/hotel-bookings/hotel-bookings.service';
import { HotelBooking } from '@app/hotels/hotel-bookings/hotel-booking.model';
import * as moment from 'moment';
import { environment } from '@env/environment';
import { ActivitiesService } from '@app/shared/components/activities/activities.service';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { NgScrollbar } from 'ngx-scrollbar';

const log = new Logger('HotelBookingCancellationsViewComponent');

@Component({
  selector: 'app-hotel-booking-cancellations-view',
  templateUrl: './hotel-booking-cancellations-view.component.html',
  styleUrls: ['./hotel-booking-cancellations-view.component.scss'],
})
export class HotelBookingCancellationsViewComponent implements OnInit, OnDestroy {
  data: HotelBookingCancellation;
  moment = moment();
  env = environment;
  hotelBookingCancellationId: number;
  LaravelModelsEnum = LaravelModelsEnum;

  @ViewChild('perfectScroll', { static: true }) perfectScroll: NgScrollbar;

  constructor(
    public activitiesService: ActivitiesService,
    private route: ActivatedRoute,
    private router: Router,
    private hotelBookingCancellationService: HotelBookingCancellationsService,
    private hotelBookingService: HotelBookingsService
  ) {}

  ngOnInit() {
    this.route.queryParamMap.subscribe((params) => {
      if (params.get('id')) {
        this.hotelBookingCancellationId = Number(params.get('id'));
        this.loadCancellationData(Number(params.get('id')));
        this.activitiesService.auto(LaravelModelsEnum.HOTELBOOKINGCANCELLATION, this.hotelBookingCancellationId, false);
      } else if (params.get('booking_id')) {
        this.loadBookingData(Number(params.get('booking_id')));
      } else {
        log.error('Id or booking_id is not found in route params!');
      }
    });
  }

  ngOnDestroy(): void {
    // крие sidebar-a s activity-тата и съобщенията
    this.activitiesService.hide();
  }

  /**
   * Load data from API
   */
  loadCancellationData(id: number): void {
    log.debug('loadCancellationData');

    const apiQuery = new ApiQuery()
      .setLimit(99999)
      .addIncludes(
        'booking.hotel',
        'booking.events',
        'booking.supplier',
        'booking.currency',
        'booking.company',
        'booking.contracting_agent',
        'booking.reservation_agent',
        'booking.rooms.nights.net_price_currency',
        'booking.rooms.meal_plan',
        'booking.rooms.type',
        'nights'
      );

    this.hotelBookingCancellationService.show(id, apiQuery).subscribe((response: HotelBookingCancellation) => {
      this.data = new HotelBookingCancellation().deserialize(response);
      this.perfectScroll.update();
    });
  }

  loadBookingData(id: number): void {
    log.debug('loadBookingData');
    const apiQuery = new ApiQuery()
      .setLimit(99999)
      .addIncludes(
        'events',
        'rooms.nights.net_price_currency',
        'rooms.type',
        'hotel',
        'supplier',
        'company',
        'currency',
        'contracting_agent',
        'reservation_agent'
      );

    this.hotelBookingService.show(id, apiQuery).subscribe((response: HotelBooking) => {
      this.data = new HotelBookingCancellation().deserialize({ booking: response, nights: [] });
      this.perfectScroll.update();
    });
  }

  log(logg: any) {
    log.debug(logg);
  }

  /**
   *  Проверява дали Night-а е избран
   * @param id ID-то на Night-а
   */
  isNightSelected(id: number): boolean {
    return !!(this.data && this.data.nights.length > 0 && this.data.nights.find((night: Night) => night.id === id));
  }

  /**
   * Премахва или добавя към масива с Nights(може и да не е целия обект а само id)
   * Този масив this.data.nights държи Nights, който са избрани и съответно се пращат към api-то
   * @param id ID-то на night-a
   */
  nightCheckboxChange(id: number): void {
    if (this.isNightSelected(id)) {
      this.data.nights.splice(this.findNightIndex(id), 1);
    } else {
      this.data.nights.push(new Night().deserialize({ id: id }));
    }
  }

  // /**
  //  *  Проверява дали трябва да избере чекбокса на Room-а (дали всички Nights са избрани)
  //  * @param room Цялата стая
  //  */
  // isRoomSelected(room: Room): boolean {
  //   log.debug(room.nights);
  //   let allNightsSelected = true;
  //   room.nights.forEach((night: Night) => {
  //     if (!this.isNightSelected(night.id)) {
  //       allNightsSelected = false;
  //     }
  //   });
  //   return allNightsSelected;
  // }

  /**
   *  Проверява дали е всички available Nights във Room-a са избрани
   * @param room Цялата стая
   */
  isRoomSelected(room: Room): boolean {
    let selectedNights = 0;
    room.nights.forEach((night: Night) => {
      if (!night.isNightAvailable()) {
        selectedNights++;
      } else if (this.isNightSelected(night.id)) {
        selectedNights++;
      }
    });

    return room.nights.length === selectedNights;
  }

  /**
   * При промяна на чекбокса на Room-a
   * @param room Room
   */
  roomCheckboxChange(room: Room): void {
    if (this.isRoomSelected(room)) {
      room.nights.forEach((night: Night) => {
        if (night.isNightAvailable()) {
          this.data.nights.splice(this.findNightIndex(night), 1);
        }
      });
    } else {
      room.nights.forEach((night: Night) => {
        if (this.findNightIndex(night) < 0 && night.isNightAvailable()) {
          this.data.nights.push(new Night().deserialize({ id: night.id }));
        }
      });
    }
  }

  /**
   * Проверява дали е всички Nights във всички Rooms са избрани
   * Използва се за checkbox-a горе в header-a на таблицата
   */
  isAllRoomsSelected() {
    if (!this.data) {
      return false;
    }
    let allRoomsSelected = true;
    this.data.booking.rooms.forEach((room: Room) => {
      room.nights.forEach((night: Night) => {
        if (!this.isNightSelected(night.id)) {
          allRoomsSelected = false;
        }
      });
    });
    return allRoomsSelected;
  }

  /**
   * Проверява дали е всички available Nights във всички Rooms са избрани
   */
  isAllAvailableNightsSelected(): boolean {
    let allAvailableNightsSelected = true;
    this.data.booking.rooms.forEach((room: Room) => {
      room.nights.forEach((night: Night) => {
        if (night.isNightAvailable()) {
          if (!this.isNightSelected(night.id)) {
            allAvailableNightsSelected = false;
          }
        }
      });
    });
    return allAvailableNightsSelected;
  }

  /**
   * При промяна на чекбокса на всички Room-ове (горе в хедъра на таблицата)
   */
  allRoomsCheckboxChange(): void {
    if (this.isAllAvailableNightsSelected()) {
      // this.data.nights = [];

      let tempNights: Array<Night> = new Array<Night>();
      tempNights = [...tempNights, ...this.data.nights];
      tempNights.forEach((night: any) => {
        if (night.isNightAvailable()) {
          this.data.nights.splice(this.findNightIndex(night), 1);
        }
      });
    } else {
      this.data.booking.rooms.forEach((room: Room) => {
        //  this.data.nights = [...this.data.nights, ...room.nights];
        room.nights.forEach((night: Night) => {
          if (this.findNightIndex(night) < 0 && night.isNightAvailable()) {
            this.data.nights.push(night);
          }
        });
      });
    }
  }

  roomSelectedNightsCount(room: Room): number {
    let count = 0;
    room.nights.forEach((night: Night) => {
      if (this.isNightSelected(night.id)) {
        count++;
      }
    });
    return count;
  }

  update(finished: boolean = false) {
    if (finished) {
      this.data.finished = true;
    }

    if (this.data && this.data.id) {
      this.hotelBookingCancellationService.update(this.formatPost()).subscribe((response: any) => {
        log.debug(response);
        this.router.navigate(['/hotel-booking-cancellations']);
      });
    } else {
      this.hotelBookingCancellationService.store(this.formatPost()).subscribe((response: any) => {
        log.debug(response);
        this.router.navigate(['/hotel-booking-cancellations']);
      });
    }
  }

  formatPost(): HotelBookingCancellation {
    return new HotelBookingCancellation().deserialize({
      id: this.data && this.data.id ? this.data.id : null,
      finished: this.data.finished || false,
      note: this.data.note,
      hotel_booking_id: this.data && this.data.booking.id ? this.data.booking.id : null,
      nights: this.data.nights,
    });
  }

  /**
   * Отваря sidebar-a s activity-тата и съобщенията
   */
  openActivities(): void {
    this.activitiesService.show(LaravelModelsEnum.HOTELBOOKINGCANCELLATION, this.hotelBookingCancellationId, false);
  }

  /**
   * Връща индекса на Night-a. Използва се като indexOf но работи
   * Връща -1 ако не намери такъв Night
   * @param night Night обект
   */
  private findNightIndex(night: any): number {
    return this.data.nights.indexOf(
      this.data.nights.find((n: Night) => {
        if (night.id) {
          return n.id === night.id;
        } else if (night) {
          return n.id === night; // ако е подадено директно ID-то на Night-a без да е в обект
        }
        return false;
      })
    );
  }

  // private getSelected(response: any) {
  //   const formattedData = {rooms: new Array()};
  //
  //   response.nights.forEach((night: any) => {
  //     if (!formattedData.rooms.find(i => i.id === night.room.id)) {
  //       night.room.nights = [];
  //       night.room.selected = false;
  //       formattedData.rooms.push(new Room().deserialize(night.room));
  //     }
  //   });
  //   response.nights.forEach((night: any) => {
  //     const nightFormatted = Object.assign({}, night);
  //     delete nightFormatted.room;
  //     formattedData.rooms.find(i => i.id === night.room.id).nights.push(new Night().deserialize(nightFormatted));
  //   });
  //
  //   log.debug(11, formattedData);
  //   return formattedData;
  // }
}
